/**
 * Modal wall-height statistics shared between the web-app roof builder
 * and server-side multi-story registration.
 *
 * `modalWallTopY` picks the dominant wall-top elevation across a set of
 * observed tops, robust to a handful of outliers (a stairwell void or a
 * single vaulted pop-up). Roof generation uses it to place the roof
 * base; multi-story registration uses it to find a story's ceiling plane
 * (where the floor above stacks). One implementation so the two never
 * drift — previously web-app-only in
 * `web-app/src/utils/roofGeometry/footprint.js`.
 */

const WALL_TOP_BUCKET = 0.05; // 5 cm — modal-height bucket size
const MULTIMODAL_RATIO = 0.8; // runner-up bucket within 80% of winner → multimodal

/**
 * Pick the most common wall-top elevation across all observed top
 * corners. When the distribution is multimodal (e.g. an exterior-wall set
 * spanning both floors of a two-story house), fall back to the max
 * elevation so upper-story walls don't poke through the roof. For a
 * single-story set with one or two anomalous walls (stairwell void,
 * vaulted pop-up) this picks the dominant ceiling height instead of being
 * dragged up by the outliers.
 *
 * @param {number[]} tops — wall-top Y elevations (absolute world Y).
 * @returns {number|null} modal top-Y, or null for an empty input.
 */
const modalWallTopY = (tops) => {
  if (tops.length === 0) return null;
  const buckets = new Map();
  for (const y of tops) {
    const key = Math.round(y / WALL_TOP_BUCKET);
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }
  const sorted = [...buckets.entries()].sort((a, b) => b[1] - a[1]);
  if (sorted.length > 1 && sorted[1][1] >= sorted[0][1] * MULTIMODAL_RATIO) {
    return Math.max(...tops);
  }
  return sorted[0][0] * WALL_TOP_BUCKET;
};

/**
 * Top Y of a box-primitive wall (structure-wall shape: center.y +
 * dimensions.y/2). Mirrors `wallBaseY` from floor-bands.js.
 */
const wallTopY = (wall) =>
  (wall.center?.y ?? 0) + (wall.dimensions?.y ?? 0) / 2;

/**
 * Modal ceiling-plane Y for a set of box-primitive structure walls — the
 * elevation where the story above stacks. null if no walls.
 *
 * @param {Array} walls — structure walls ({ center, dimensions }).
 */
const modalCeilingY = (walls) => {
  if (!walls || walls.length === 0) return null;
  return modalWallTopY(walls.map(wallTopY));
};

/**
 * Is a wall's top edge one horizontal line? Crown molding (and anything
 * else that runs along a level top) is only valid on such walls — a
 * raked gable side or a stepped top cannot carry it.
 *
 * Top corners are the polygon corners above the wall's mid-height; the
 * top is flat when they all sit within `tol` of each other. Tolerance
 * defaults to WALL_TOP_BUCKET (5 cm), the same bucket the modal-top
 * stats treat as "one elevation". A wall without a polygon outline (box
 * primitive) is flat by construction. Corners may be {x, y, z} objects
 * or [x, y, z] arrays.
 */
const wallHasFlatTop = (wall, tol = WALL_TOP_BUCKET) => {
  const corners = wall?.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 3) return true;
  const ys = [];
  for (const c of corners) {
    const y = Array.isArray(c) ? c[1] : c?.y;
    if (typeof y === 'number' && Number.isFinite(y)) ys.push(y);
  }
  if (ys.length < 3) return true;
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  if (maxY - minY <= tol) return true; // degenerate sliver — nothing to rake
  const midY = (minY + maxY) / 2;
  const topYs = ys.filter((y) => y > midY);
  if (topYs.length === 0) return true;
  return Math.max(...topYs) - Math.min(...topYs) <= tol;
};

module.exports = {
  WALL_TOP_BUCKET,
  MULTIMODAL_RATIO,
  modalWallTopY,
  wallTopY,
  modalCeilingY,
  wallHasFlatTop,
};
